import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

/**
 * Unified sample-data seed/clear card. Mirrors the backend
 * /api/admin/sample-data router shape (iter367). Each domain row
 * shows present-count vs template-count, a "Seed" button, and a
 * "Clear" button that only appears when rows exist. For menu items
 * we also render a danger "Wipe ALL menu items" button so users
 * who imported a test set during first-time setup can start clean.
 */
export default function SampleDataCard() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${domain}:${action}` | null
  const [wiping, setWiping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/sample-data');
      setDomains(data?.domains || []);
    } catch {
      toast.error('Failed to load sample-data state');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const seed = async (key) => {
    setBusy(`${key}:seed`);
    try {
      const { data } = await api.post(`/admin/sample-data/${key}/seed`);
      toast.success(`Seeded ${data.inserted} sample rows`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Seed failed');
    } finally {
      setBusy(null);
    }
  };

  const clear = async (key) => {
    if (!window.confirm('Remove all sample rows for this domain?')) return;
    setBusy(`${key}:clear`);
    try {
      const { data } = await api.delete(`/admin/sample-data/${key}/clear`);
      toast.success(`Removed ${data.removed} sample rows`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed');
    } finally {
      setBusy(null);
    }
  };

  const wipeMenu = async () => {
    const first = window.confirm('Wipe ALL menu items (not just samples) for this tenant?');
    if (!first) return;
    const second = window.prompt('Type DELETE to confirm you want to wipe every menu item:');
    if (second !== 'DELETE') {
      toast.info('Wipe cancelled');
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post('/admin/sample-data/menu-items/wipe-test-data', { confirm: 'DELETE' });
      toast.success(`Removed ${data.removed} menu items`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Wipe failed');
    } finally {
      setWiping(false);
    }
  };

  return (
    <Card className="border-border" data-testid="sample-data-card">
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Sparkles size={18} className="text-violet-500" />
          Sample / Test Data
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Seed a tiny set of demo rows so you can explore a module
          without real data. Clear it any time — sample rows are
          tagged and won't touch your real records.
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && domains.map((d) => (
          <div
            key={d.key}
            className="flex items-center justify-between p-4 border rounded-xl hover:bg-muted/50 transition-all"
            data-testid={`sample-row-${d.key}`}
          >
            <div className="min-w-0">
              <div className="font-medium text-sm flex items-center gap-2">
                {d.label}
                {d.sample_count > 0 && (
                  <Badge variant="secondary" className="text-[10px]" data-testid={`sample-count-${d.key}`}>
                    {d.sample_count} present
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                Seeds {d.sample_template_count} sample row{d.sample_template_count === 1 ? '' : 's'} into
                <code className="ml-1 px-1 rounded bg-muted text-[10px]">{d.collection}</code>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="rounded-xl text-xs"
                disabled={busy === `${d.key}:seed`}
                onClick={() => seed(d.key)}
                data-testid={`sample-seed-${d.key}`}
              >
                {busy === `${d.key}:seed` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                Seed
              </Button>
              {d.sample_count > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-xl text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  disabled={busy === `${d.key}:clear`}
                  onClick={() => clear(d.key)}
                  data-testid={`sample-clear-${d.key}`}
                >
                  {busy === `${d.key}:clear` ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
                  Clear
                </Button>
              )}
            </div>
          </div>
        ))}

        <div className="p-4 border border-rose-200 bg-rose-50/50 rounded-xl flex items-start justify-between gap-4" data-testid="wipe-menu-panel">
          <div className="flex gap-3 min-w-0">
            <AlertTriangle size={18} className="text-rose-600 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-rose-800">Start fresh with menu items</div>
              <p className="text-xs text-rose-700/80 mt-1">
                Removes every menu item (including ones you imported),
                not just samples. Useful when you've been testing an
                import and want a clean slate before real launch.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="rounded-xl text-xs border-rose-200 text-rose-700 hover:bg-rose-100 shrink-0"
            disabled={wiping}
            onClick={wipeMenu}
            data-testid="wipe-menu-btn"
          >
            {wiping ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
            Wipe ALL menu items
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
